import {Reducer} from "../../lib/redux";
import {DvirLogActions} from "./actions";
import {DvirLogActionTypes} from "./types";
import { IDVIRLog } from "../../model/DVIRLog";
import { extend } from "../../utils/vanilla";
import { getRepairNeededByAsset } from "../../model/dvirRepair";

export let defaultDvirLogs: IIdDictionary<IIdDictionary<IDVIRLog>> = {};

/**
 * True for a log whose changes have not reached the server: a queued creation or edit that this
 * device holds the only copy of.
 *
 * Session cleanup must not discard these. For an inspection signed offline (`modified: "Add"`) the
 * device holds the only copy, so deleting it destroys completed driver work outright — see
 * ESUPSD-52472, where a post-trip vanished between being signed and the next successful sync.
 *
 * Deliberately limited to "Add" and "Set": pushChangedToServer only ever sends those two, and
 * sync.dvirs exposes no removeItem, so nothing in production writes a DVIR with modified "Remove".
 * Retaining an unknown or corrupt modified value would keep a record no sync can ever reconcile.
 *
 * Retention is not delivery — DVIR is not in syncsForLogin, so the record waits until the next
 * DVIR sync this driver becomes eligible for, and prune() can still discard it (MYG-143888).
 */
export const isUnsyncedDvirLog = (log?: DeepReadonlyObject<IDVIRLog> | IDVIRLog): boolean => {
    return Boolean(log && (log.modified === "Add" || log.modified === "Set"));
};

/**
 * Ids of the logs in `logs` whose changes have not reached the server. Exported so that callers
 * reporting on retention use the same predicate the reducer retains by — if the two drift, a caller
 * ends up describing a set that does not match what was actually kept.
 */
export const getUnsyncedDvirLogIds = (logs?: DeepReadonlyObject<IIdDictionary<IDVIRLog>>): string[] => {
    return Object.keys(logs || {}).filter(logId => isUnsyncedDvirLog(logs?.[logId]));
};

const getUnsyncedLogs = (logs: IIdDictionary<IDVIRLog>): IIdDictionary<IDVIRLog> => {
    return getUnsyncedDvirLogIds(logs).reduce((unsynced, logId) => {
        unsynced[logId] = logs[logId];
        return unsynced;
    }, {} as IIdDictionary<IDVIRLog>);
};

/**
 * The logs that outlive a `clearDvirLogs` pass: kept users keep everything, every other user keeps
 * whatever has not reached the server.
 *
 * This is the reducer's own rule, exported because callers need to know what survives *before* the
 * dispatch happens — session cleanup has to decide which media to keep alongside these logs. Naming
 * only the current user and the kept users is not the same rule: a driver removed from the user
 * service by an earlier cleanup is neither, yet their unsynced logs are retained here every time,
 * and media matched against a narrower set would be dropped out from under them (MYG-144154).
 */
export const getLogsSurvivingClear = (
    state: DeepReadonlyObject<IIdDictionary<IIdDictionary<IDVIRLog>>> | IIdDictionary<IIdDictionary<IDVIRLog>>,
    keepUserIds: string[]
): IIdDictionary<IIdDictionary<IDVIRLog>> => {
    // Accepts the store's readonly view as well as the reducer's mutable state. Safe to narrow
    // once here because nothing below writes through it — the result re-references the same logs
    // rather than copying them, which is what lets session cleanup ask this question during logout
    // without deep-cloning every log on the device. The corollary is that the result aliases
    // whatever was passed in: called with the store's view, its logs are store state, so callers
    // must read them and never write.
    const logsByUser = state as IIdDictionary<IIdDictionary<IDVIRLog>>;

    return Object.keys(logsByUser).reduce((remaining, userId) => {
        if (keepUserIds.indexOf(userId) !== -1) {
            remaining[userId] = logsByUser[userId];
            return remaining;
        }

        const unsyncedLogs = getUnsyncedLogs(logsByUser[userId]);
        if (Object.keys(unsyncedLogs).length > 0) {
            remaining[userId] = unsyncedLogs;
        }
        return remaining;
    }, {} as IIdDictionary<IIdDictionary<IDVIRLog>>);
};

export let dvirLogs: Reducer<IIdDictionary<IIdDictionary<IDVIRLog>>, DvirLogActions> = (state = defaultDvirLogs, action) => {
    switch (action.type) {
        case DvirLogActionTypes.SET_DVIR_LOGS: {
            const userId = action.payload.userId;
            const logs = action.payload.logs ? extend(true, {}, action.payload.logs) : {};
            if (userId) {
                return {
                    ...state,
                    [userId]: logs
                };
            }
            return state;
        }
        case DvirLogActionTypes.CLEAR_DVIR_LOGS: {
            const keepUserIds = (action.payload.keepUsers || []).map(user => user.id);

            // Users being cleared keep their unsynced logs so nothing that exists only on this
            // device is lost; everything already on the server is dropped as before.
            return getLogsSurvivingClear(state, keepUserIds);
        }
        default:
            return state;
    }
};

export let defaultDvirRepairNeeded: IIdDictionary<IIdDictionary<boolean>> = {};

/**
 * Per user, the assets (vehicle or trailer) whose latest DVIR log needs repair, kept in step with
 * dvirLogs by the same actions. Derived for the native dashboard, which cannot compute it; the web
 * app does not read it. A user's entry exists once their logs have been set (an empty map means none
 * need repair), so "not reported yet" and "none" differ.
 */
export let dvirRepairNeeded: Reducer<IIdDictionary<IIdDictionary<boolean>>, DvirLogActions> = (state = defaultDvirRepairNeeded, action) => {
    switch (action.type) {
        case DvirLogActionTypes.SET_DVIR_LOGS: {
            const userId = action.payload.userId;
            return userId ? { ...state, [userId]: getRepairNeededByAsset(action.payload.logs) } : state;
        }
        case DvirLogActionTypes.CLEAR_DVIR_LOGS: {
            // Kept users keep their entry. Others keep only unsynced logs, which this reducer cannot see;
            // their entry is dropped and recomputed by the next SET_DVIR_LOGS for that user.
            const keepUserIds = (action.payload.keepUsers || []).map(user => user.id);
            return Object.keys(state).reduce((kept, userId) => {
                if (keepUserIds.indexOf(userId) !== -1) {
                    kept[userId] = state[userId];
                }
                return kept;
            }, {} as IIdDictionary<IIdDictionary<boolean>>);
        }
        default:
            return state;
    }
};
